import React from 'react'
import {
  FaArrowRight,
  FaStar,
  FaClock,
  FaMapMarkerAlt,
  FaTag,
  FaGift,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { cmsApi } from '../../services/cms'
import { useCMS } from '../../hooks/useCMS'
import { isMatchingCountry, type CountrySelection } from '../../utils/countryFilter'

type CampaignPick = {
  id?: string
  slug?: string
  campaignType?: string
  sectionKey?: string
  category?: string
  title?: string
  name?: string
  subtitle?: string
  description?: string
  originalPrice?: string | number
  discountedPrice?: string | number
  duration?: string
  expiresOn?: string
  highlights?: string[]
  tags?: string[]
  imageUrl?: string
  ctaUrl?: string
  buttonText?: string
  ctaText?: string
  badgeText?: string
  tag?: string
  referenceId?: string
  referenceName?: string
  countries?: CountrySelection
  country?: string | null
  leadCountry?: string | null
  siteCountry?: string | null
  sourceCountry?: string | null
  market?: string | null
}

const normalizeCampaignValue = (value?: string) =>
  (value || '').toLowerCase().replace(/[\s_-]+/g, '')

const isEarlyBirdPick = (pick: CampaignPick) => {
  const campaignType = normalizeCampaignValue(pick.campaignType)
  const sectionKey = normalizeCampaignValue(pick.sectionKey)

  return campaignType.includes('earlybird') || sectionKey.includes('earlybird')
}

const formatPrice = (value?: string | number) => {
  if (value === undefined || value === null || value === '') return ''
  return typeof value === 'number' ? value.toLocaleString() : value
}

const formatExpiryDate = (value?: string) => {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return value

  return parsedDate.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

type RefRecord = { id?: string; slug?: string }

const buildSlugMap = (records: RefRecord[]): Map<string, string> => {
  const map = new Map<string, string>()
  records.forEach((r) => { if (r.id && r.slug) map.set(r.id, r.slug) })
  return map
}

const hasCountrySelection = (selection: CountrySelection) => {
  if (Array.isArray(selection)) {
    return selection.some((entry) => String(entry || '').trim().length > 0)
  }

  if (typeof selection === 'string') {
    return selection
      .split(/[,|;/]+/)
      .some((entry) => entry.trim().length > 0)
  }

  return false
}

const getCountrySelection = (record: {
  countries?: CountrySelection
  country?: string | null
  leadCountry?: string | null
  siteCountry?: string | null
  sourceCountry?: string | null
  market?: string | null
}): CountrySelection =>
  record.countries ||
  record.country ||
  record.leadCountry ||
  record.siteCountry ||
  record.sourceCountry ||
  record.market ||
  null

const EarlyBirds: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const siteRegion = import.meta.env.VITE_SITE_REGION || 'IN'
  const { data: featuredPicks, loading } = useCMS<CampaignPick[]>(() =>
    cmsApi.getFeaturedPicks({ campaignType: 'early_bird' })
  )
  const { data: destinations } = useCMS<RefRecord[]>(() =>
    cmsApi.getDestinations('isActive=true')
  )
  const { data: visaDestinations } = useCMS<RefRecord[]>(() =>
    cmsApi.getVisaDestinations()
  )

  if (loading) return <div className="py-20 text-center text-white">Loading...</div>

  const destinationSlugMap = buildSlugMap(
    (destinations || []).filter((destination: any) => {
      const selection = getCountrySelection(destination)
      return hasCountrySelection(selection) && isMatchingCountry(selection, siteRegion)
    }),
  )
  const visaSlugMap = buildSlugMap(
    (visaDestinations || []).filter((destination: any) => {
      const selection = getCountrySelection(destination)
      return hasCountrySelection(selection) && isMatchingCountry(selection, siteRegion)
    }),
  )

  const resolveDestination = (pick: CampaignPick): string => {
    // if (pick.ctaUrl) {
    //   try { return new URL(pick.ctaUrl).pathname } catch { return pick.ctaUrl }
    // }
    const type = normalizeCampaignValue(pick.category || '')
    if (type.includes('visa')) {
      const slug = pick.referenceId ? visaSlugMap.get(pick.referenceId) : undefined
      return slug ? `/visa-services/${slug}` : '/visa-services'
    }
    const slug = pick.referenceId ? destinationSlugMap.get(pick.referenceId) : undefined
    return slug ? `/destination-detail/${slug}` : '/destination'
  }

  const offers = (featuredPicks || [])
    .filter((pick) => {
      const selection = getCountrySelection(pick)
      return hasCountrySelection(selection) && isMatchingCountry(selection, siteRegion)
    })
    .filter(isEarlyBirdPick)
    .slice(0, 2)

  return (
    <section className='relative overflow-hidden bg-brand-dark py-20 text-white'>
      <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,0,101,0.2),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_30%)]' />
      <div className='absolute right-0 top-0 h-full w-1/2 pointer-events-none opacity-10'>
        <svg viewBox='0 0 200 200' className='h-full w-full'>
          <path
            fill='#ef0065'
            d='M44.7,-76.4C58.8,-69.2,71.8,-59.1,81.3,-46.3C90.8,-33.5,96.8,-18,97.1,-2.3C97.4,13.4,92,29.3,82.4,42.4C72.8,55.5,59,65.8,44.2,73.4C29.4,81,14.7,85.9,0.3,85.4C-14.1,84.9,-28.2,79,-41.8,71.5C-55.4,64,-68.5,54.9,-77.4,42.8C-86.3,30.7,-91,15.4,-91.5,0C-92,-15.4,-88.3,-30.8,-80.1,-43.6C-71.9,-56.4,-59.2,-66.6,-45.5,-74.1C-31.8,-81.6,-15.9,-86.4,-0.2,-86C15.5,-85.6,30.6,-83.6,44.7,-76.4Z'
            transform='translate(100 100)'
          />
        </svg>
      </div>

      <div className='relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <div className='inline-flex items-center gap-2 rounded-full border border-brand-pink/30 bg-brand-pink/15 px-4 py-2'>
            <FaTag className='text-sm text-brand-pink' />
            <span className='text-brand-pink text-xs font-semibold uppercase tracking-wider sm:text-sm'>
              {t('Early Bird Special')}
            </span>
          </div>

          <h2 className='font-heading mt-5 text-3xl font-bold md:text-4xl lg:text-5xl'>
            {t('Early Bird')}{' '}
            <span className='italic text-brand-pink'>{t('Offers')}</span>
          </h2>

          <p className='mt-4 mx-auto max-w-2xl text-base leading-7 text-gray-300'>
            {t('Book in advance and enjoy exclusive discounts on premium safari experiences. Limited time offer.')}
          </p>
        </div>

        <div className='flex justify-center'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 w-fit'>
            {offers.map((offer) => {
              const tags = offer.tags || []
              const highlights = offer.highlights || []
              const originalPrice = formatPrice(offer.originalPrice)
              const discountedPrice = formatPrice(offer.discountedPrice)
              const expiresOn = formatExpiryDate(offer.expiresOn)

              const destination = resolveDestination(offer)

              return (
                <div
                  key={offer.id || offer.slug || offer.title || offer.name}
                  role='link'
                  tabIndex={0}
                  onClick={() => navigate(destination)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      navigate(destination)
                    }
                  }}
                  className='group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-pink/40 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-brand-pink/60'
                >
                  <div className='relative h-48 overflow-hidden'>
                    <img
                      src={offer.imageUrl}
                      alt={offer.title || offer.name}
                      className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105'
                    />
                    <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent' />

                    <div className='absolute inset-x-0 top-0 flex items-start justify-between p-3'>
                      <span className='inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur-sm border border-white/20'>
                        <FaTag className='text-brand-pink text-xs' />
                        {offer.badgeText || offer.tag || t('Early Bird')}
                      </span>

                      <span className='rounded-full bg-brand-pink px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-lg'>
                        {t('Special Offer')}
                      </span>
                    </div>

                    <div className='absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold text-brand-dark shadow-sm'>
                      <FaStar className='text-yellow-400 text-[10px]' />
                      4.9
                    </div>
                  </div>

                  <div className='flex flex-1 flex-col p-4 justify-between'>
                    <div className='mb-3 flex-shrink-0'>
                      <h3 className='font-heading text-lg font-bold text-white leading-tight group-hover:text-brand-pink transition-colors'>
                        {offer.title || offer.name}
                      </h3>
                      <p className='mt-1 flex items-center gap-1.5 text-xs text-gray-300'>
                        <FaMapMarkerAlt className='text-[10px] text-brand-pink' />
                        {offer.subtitle}
                      </p>
                    </div>

                    <div className='mb-3 flex flex-wrap gap-2'>
                      <span className='inline-flex items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-xs text-gray-200 border border-white/10'>
                        <FaClock className='text-[10px]' />
                        {offer.duration ? t(offer.duration) : t('Limited Time')}
                      </span>
                      {(tags.length > 0 ? tags : ['Flights Included', 'Family Friendly'])
                        .slice(0, 2)
                        .map((tag, index) => (
                          <span
                            key={`${tag}-${index}`}
                            className='inline-flex items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-xs text-gray-200 border border-white/10'
                          >
                            <FaTag className='text-[10px]' />
                            {t(tag)}
                          </span>
                        ))}
                    </div>

                    {/* <p className='mb-3 text-xs text-gray-300 leading-relaxed line-clamp-2'>
                      {offer.description ? t(offer.description) : t('Book now to unlock this early bird offer.')}
                    </p> */}

                    {highlights.length > 0 && (
                      <div className='mb-4 flex flex-wrap gap-1.5'>
                        {highlights.slice(0, 2).map((highlight, index) => (
                          <span
                            key={`${highlight}-${index}`}
                            className='rounded-full bg-brand-pink/10 px-2.5 py-1 text-[11px] font-medium text-brand-pink border border-brand-pink/30'
                          >
                            {t(highlight)}
                          </span>
                        ))}
                        {highlights.length > 2 && (
                          <button
                            type='button'
                            onClick={(event) => event.stopPropagation()}
                            className='rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-medium text-gray-300 hover:text-brand-pink transition-colors border border-white/10'
                          >
                            +{highlights.length - 2} {t('more')}
                          </button>
                        )}
                      </div>
                    )}

                    <div className='mb-4 rounded-lg bg-white/5 border border-brand-pink/30 p-3'>
                      <div className='flex items-center justify-between gap-2'>
                        <div className='flex-1'>
                          <p className='text-[10px] uppercase tracking-wider text-gray-400'>
                            {t('From')}
                          </p>
                          <div className='flex items-baseline gap-2'>
                            {originalPrice && (
                              <span className='text-xs text-gray-400 line-through'>
                                {originalPrice}
                              </span>
                            )}
                            <span className='text-brand-pink text-lg font-bold'>
                              {discountedPrice || t('Contact Us')}
                            </span>
                          </div>
                        </div>
                        <div className='h-8 w-px bg-white/10'></div>
                        <div className='flex-1'>
                          <p className='text-[10px] uppercase tracking-wider text-gray-400 mb-1'>
                            {t('Expires')}
                          </p>
                          <p className='text-xs font-bold text-brand-pink'>
                            {expiresOn || t('Limited Time')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      type='button'
                      onClick={(event) => {
                        event.stopPropagation()
                        navigate(destination)
                      }}
                      className='w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-brand-pink to-brand-pink/90 py-2.5 px-4 font-semibold text-white transition-all duration-300 hover:shadow-lg hover:from-brand-pink/90 hover:to-brand-pink/80 active:scale-95'
                    >
                      <span className='text-sm'>{offer.buttonText || offer.ctaText || t('Claim Offer')}</span>
                      <FaArrowRight className='text-xs transition-transform group-hover:translate-x-0.5' />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className='mt-12 text-center'>
          <div className='inline-flex flex-wrap items-center justify-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm'>
            <div className='flex items-center gap-2 text-sm text-gray-200'>
              <FaClock className='text-brand-pink' />
              <span>{t('Book 60+ days in advance')}</span>
            </div>
            <div className='hidden h-4 w-px bg-white/20 sm:block'></div>
            <div className='flex items-center gap-2 text-sm text-gray-200'>
              <FaTag className='text-brand-pink' />
              <span>{t('Save up to 30%')}</span>
            </div>
            <div className='hidden h-4 w-px bg-white/20 sm:block'></div>
            <div className='flex items-center gap-2 text-sm text-gray-200'>
              <FaGift className='text-brand-pink' />
              <span>{t('Free airport transfers')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default EarlyBirds
